import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ICarrier } from 'src/app/models/ICarrier.model';
import { IWorker } from 'src/app/models/IWorker.model';
import { Orders } from 'src/app/models/Orders.model';
import { Priority } from 'src/app/models/Priority.model';
import { Products } from 'src/app/models/Product.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { ProductsService } from 'src/app/services/products.service';
import { DropdownService } from 'src/app/services/shared/dropdown.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * AssegnaOrdineDettagliComponent — modale di assegnazione di un
 * ordine nuovo (lanciata da AssegnaOrdineListComponent, Chunk I.1).
 *
 * Pattern visivamente identico a `ordini-parziali-dettagli` E.2
 * (v2.8.0) ma con 2 extra:
 *   • `expected_shipping_date` readonly nelle info ordine
 *   • picking list con `sub_products` nested expandable (per i
 *     prodotti compositi con qty_needed × op_qta_picked)
 *
 * Lascia all'admin:
 *   • leggere i dati ordine (cliente + indirizzo + note + consegna
 *     prevista)
 *   • assegnare operatore, priorità e corriere
 *   • selezionare i prodotti da prelevare con sub_products
 *     auto-calcolati (osp_qty = qty_needed × op_qta_picked)
 *   • inviare il payload `IOrderToSend` al BE
 *
 * Sub-modale: `app-product-detail` (apre la posizione magazzino di un
 * prodotto cliccato).
 *
 * Phase 1.5 / v2.10.0 — TS cleaned up (Chunk I.2):
 *   • dropped kendo-dialog/kendo-formfield/kendo-combobox/
 *     kendo-dropdownlist/kendoTextBox (55 kendo refs → 0 in HTML),
 *   • dropped 25 console.log/debug ("Date now is", "Sandwich" 🥪,
 *     "Bastard" 😅, "ce caat" 🤦, "PRODUCTS NEW ARE", "Quote date
 *     is", "It is invalid!", debug noise sparso),
 *   • dropped `addOrderDialog` (~50 LOC dead, ricostruiva form mai
 *     usato con regex email su carrier_id 😱 — bug identico al
 *     legacy di E.2),
 *   • dropped 5 handler dead: `clearForm`, `goBackFunction` +
 *     `goBack` (router.navigate da modale errato, simile a E.2
 *     legacy), `open`, `modify` (isEditable toggle mai usato in
 *     template), `updateSubProduct` (handler con tonnellate di
 *     codice commentato, fondamentalmente dead),
 *   • dropped 7 state morti: `dataSaved`, `addOrder`, `assignOrder`,
 *     `selectedProds`, `chosenProd`, `isEditable`, `orderToSend`,
 *   • dropped dialog dimensions hardcoded (`minWidth=1400`,
 *     `dialogWidth=600`, `dialogHeight=1017`, `maxlength=250`),
 *   • fixed `carrier_id` → `or_carrier_id` (formControlName allineato
 *     con `Orders.IOrderToSend.or_carrier_id` — bug latente identico
 *     al legacy di E.2),
 *   • 4 subscribe annidate in ngOnInit → forkJoin singolo
 *     (`takeUntilDestroyed`) — memory leak fix,
 *   • `changeProductNumber` rifatto come `qtyOptions` pre-computed
 *     map (era invocato ad ogni click sulla dropdown),
 *   • Clamp `op_qta_picked` ≤ `op_qta_ordered`,
 *   • Sub-products quantity auto-recompute estratto in helper
 *     `recomputeSubProductsQty()`,
 *   • switch a `inject()` + `ChangeDetectorRef.detectChanges()` su
 *     state mutation in callback.
 *
 * Behaviour preserved 1:1:
 *   • payload `addOrderMethod` (incluso `or_agent_id:'1'` e
 *     `or_customer_reference:'123'` — convenzione BE per assegnazione
 *     manuale via admin, identico a E.2),
 *   • `or_assignment_date` settato a `today.toISOString()` al
 *     submit (NB: diversa da E.2 che usa `orderNew.or_order_date`),
 *   • `or_partial` recomputed dopo ogni pick (S = parziale, N =
 *     pieno),
 *   • sub_products `opCheckedOnAssegna` toggle + osp_qty recompute.
 */
@Component({
  selector: 'app-assegna-ordine-dettagli',
  templateUrl: './assegna-ordine-dettagli.component.html',
  styleUrls: ['./assegna-ordine-dettagli.component.scss'],
  standalone: false,
})


export class AssegnaOrdineDettagliComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly productServ = inject(ProductsService);
  private readonly dropdownServ = inject(DropdownService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs / Outputs ──────────────────────────────────────────────
  @Input() isAssegnaOn = false;
  @Input() orderId = -1;
  @Output() result = new EventEmitter<boolean>();

  // ── Form ──────────────────────────────────────────────────────────
  public assignOrderForm!: FormGroup;

  // ── Data ──────────────────────────────────────────────────────────
  public orderNew!: Orders.IOrderNew;
  public productsNew: Products.IProductToSend[] = [];
  public priorityOptions: Priority.IPriority[] = [];
  public carriers: ICarrier[] = [];
  public workers: IWorker[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public sending = false;
  public allSelected = false;
  /** S = ordine resta parziale (qualcosa non prelevato) · N = chiuso */
  public partialOrder: 'S' | 'N' | null = null;
  public formattedDate = '—';
  public formattedExpectedDate = '—';
  /** Map: op_code → array di quantità selezionabili (1..op_qta). */
  public qtyOptions: Record<string, number[]> = {};
  /** Map: op_code → sub_products expanded? */
  public expandedSubProducts: Record<string, boolean> = {};
  public clickedProductId: number | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Lunghezza massima della nota assegnazione. Deve restare allineata
   * all'attributo `maxlength="250"` dell'input nel template:
   * Angular attiva automaticamente il directive `MaxLengthValidator`
   * su `[maxlength][formControlName]`, quindi il control eredita
   * implicitamente il validator anche se non lo dichiarassimo qui.
   *
   * 🚨 FIX 2026-07-14 (v2.14.6 — bug silente segnalato da Gabri):
   * pre-fix `patchValue({ or_note: this.orderNew.or_note })` caricava
   * il valore BE senza truncate. Se il BE ritornava una nota > 250
   * (nessun vincolo server-side), `MaxLengthValidator(250)` scattava,
   * `form.valid` diventava false e il bottone "Pronto per l'operatore"
   * restava disabled anche completando tutti i campi required.
   * Sintomo utente: svuotando la nota (o cancellando + digitando,
   * bloccato a 250 char dall'attributo HTML5) il pulsante si sbloccava.
   * Fix: (a) `Validators.maxLength` esplicito nel FormControl per
   * rendere il contract chiaro anche a chi legge il TS senza HTML,
   * (b) truncate difensivo nel patchValue.
   */
  private static readonly NOTE_MAX_LENGTH = 250;

  ngOnInit(): void {
    this.assignOrderForm = this.formBuilder.group({
      or_order_number: [''],
      or_order_date: [''],
      or_order_priority_cod: ['', Validators.required],
      or_carrier_id: [''],
      or_customer: [''],
      or_shipping_address: [''],
      or_shipping_zip: [''],
      expected_shipping_date: [''],
      or_shipping_city: [''],
      or_shipping_prov: [''],
      or_shipping_country: [''],
      or_assigned_id: ['', Validators.required],
      or_note: [
        '',
        Validators.maxLength(AssegnaOrdineDettagliComponent.NOTE_MAX_LENGTH),
      ],
    });

    this.loadOrder();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loading — order + dropdowns in parallel.
  // ──────────────────────────────────────────────────────────────────

  private loadOrder(): void {
    this.loading = true;

    forkJoin({
      order: this.ordersServ.getNewOrder(String(this.orderId)),
      priorities: this.dropdownServ.getPriorities(),
      carriers: this.dropdownServ.getCarriers(),
      workers: this.dropdownServ.getWorkers(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ order, priorities, carriers, workers }) => {
          this.orderNew = new Orders.OrderNew((order as IWsResult).data);
          this.productsNew = this.buildPickingProducts(this.orderNew);
          this.qtyOptions = this.buildAllQtyOptions(this.productsNew);

          this.priorityOptions = priorities;
          this.carriers = carriers;
          this.workers = (workers ?? []).map((w) => ({
            ...w,
            full_name: `${w.ac_first_name} ${w.ac_last_name}`,
          }));

          if (this.orderNew.or_order_date) {
            this.formattedDate = formatDate(
              this.orderNew.or_order_date,
              'dd/MM/yyyy',
              'it',
            );
          }
          if (this.orderNew.expected_shipping_date) {
            this.formattedExpectedDate = formatDate(
              this.orderNew.expected_shipping_date,
              'dd/MM/yyyy',
              'it',
            );
          }

          this.assignOrderForm.patchValue({
            or_order_number: this.orderNew.or_order_number,
            or_order_date: this.formattedDate,
            or_carrier_id: this.orderNew.or_carrier_id ?? '',
            or_customer: this.orderNew.or_customer,
            or_shipping_address: this.orderNew.or_shipping_address,
            or_shipping_zip: this.orderNew.or_shipping_zip,
            or_shipping_city: this.orderNew.or_shipping_city,
            or_shipping_prov: this.orderNew.or_shipping_prov,
            or_shipping_country: this.orderNew.or_shipping_country,
            // Fix v2.14.6: truncate difensivo — se il BE ritorna una
            // nota > NOTE_MAX_LENGTH (250) il MaxLengthValidator
            // implicito boccherebbe il form. Vedi doc su NOTE_MAX_LENGTH.
            or_note: (this.orderNew.or_note ?? '')
              .toString()
              .slice(0, AssegnaOrdineDettagliComponent.NOTE_MAX_LENGTH),
            expected_shipping_date: this.formattedExpectedDate,
          });

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Impossibile caricare i dati dell\'ordine.',
            timer: 1800,
            timerProgressBar: true,
          });
        },
      });
  }

  /** Maps the IOrderNew.products[] (server shape) to the picking
   *  shape used in this modal: starts with op_checked=false and
   *  op_qta_picked = op_qta (full quantity). */
  private buildPickingProducts(
    order: Orders.IOrderNew,
  ): Products.IProductToSend[] {
    return (order.products ?? []).map((item: any) => {
      const product: any = {
        op_note: '',
        op_checked: false,
        op_code: item.op_code,
        op_description: item.op_description,
        op_qta: item.op_qta,
        orQtaNeeded: item.op_qta,
        op_weigth: item.op_weigth,
        op_stock: item.op_stock,
        op_qta_picked: +item.op_qta,
        op_qta_ordered: item.op_qta_ordered,
        op_positions: item.op_positions,
        tab_order_status_cod: item.tab_order_status_cod,
        sub_products: item?.sub_products ?? [],
      };
      // Recompute sub_products initial osp_qty (qty_needed × op_qta).
      product.sub_products.forEach((sub: any) => {
        sub.osp_qty = +(sub?.qty_needed ?? 0) * +item.op_qta;
        sub.osp_qty_ordered = +(sub?.qty_needed ?? 0) * +item.op_qta_ordered;
      });
      return product;
    });
  }

  /** Pre-computes 1..op_qta arrays for each product code so the
   *  template doesn't rebuild them at every dropdown click. */
  private buildAllQtyOptions(
    products: Products.IProductToSend[],
  ): Record<string, number[]> {
    const map: Record<string, number[]> = {};
    for (const p of products) {
      const n = Number(p.op_qta) || 0;
      map[p.op_code] = Array.from({ length: n }, (_, i) => i + 1);
    }
    return map;
  }

  // ──────────────────────────────────────────────────────────────────
  // Picking actions.
  // ──────────────────────────────────────────────────────────────────

  togglePicked(index: number): void {
    const p: any = this.productsNew[index];
    p.op_checked = !p.op_checked;
    // Mirror sub_products opCheckedOnAssegna flag.
    p.sub_products?.forEach((sub: any) => {
      sub.opCheckedOnAssegna = p.op_checked;
    });
    this.updateNullCodeProductStates();
    this.recomputePartialFlag();
    this.recomputeAllSelected();
  }

  /** Some products have null op_code and need to mirror the checked
   *  state of preceding "real" products. */
  private updateNullCodeProductStates(): void {
    this.productsNew.forEach((product: any, index) => {
      if (product.op_code == null) {
        const above = this.productsNew.slice(0, index);
        const anySelectedAbove = above.some(
          (p: any) =>
            p.op_checked && ((+p.op_qta || 0) > 0 || (+p.op_qta_picked || 0) > 0),
        );
        product.op_checked = anySelectedAbove;
      }
    });
  }

  selectAllProducts(): void {
    this.allSelected = !this.allSelected;
    this.productsNew.forEach((item: any) => {
      item.op_checked = this.allSelected;
      item.sub_products?.forEach((sub: any) => {
        sub.opCheckedOnAssegna = this.allSelected;
      });
    });
    this.recomputePartialFlag();
  }

  /** Called when the user picks a new quantity from the dropdown.
   *  Auto-marks the product as picked, clamps below op_qta, and
   *  recomputes sub_products osp_qty based on qty_needed × picked. */
  updateQuantity(picked: number, index: number): void {
    const p: any = this.productsNew[index];
    const max = Number(p.op_qta) || 0;
    p.op_qta_picked = Math.min(Math.max(0, Number(picked) || 0), max);
    p.op_checked = true;
    this.recomputeSubProductsQty(p);
    this.recomputePartialFlag();
    this.recomputeAllSelected();
  }

  /** Recompute osp_qty/osp_qty_ordered for a product's sub_products
   *  based on the current op_qta_picked × qty_needed. */
  private recomputeSubProductsQty(product: any): void {
    product.sub_products?.forEach((sub: any) => {
      sub.opCheckedOnAssegna = product.op_checked;
      sub.osp_qty = +(sub?.qty_needed ?? 0) * +product.op_qta_picked;
      sub.osp_qty_ordered =
        +(sub?.qty_needed ?? 0) * +product.op_qta_picked;
    });
  }

  private recomputePartialFlag(): void {
    const allChecked = this.productsNew.every((v: any) => v.op_checked);
    const allFull = this.productsNew.every(
      (v: any) => +v.op_qta === +v.op_qta_picked,
    );
    this.partialOrder = allChecked && allFull ? 'N' : 'S';
  }

  private recomputeAllSelected(): void {
    this.allSelected =
      this.productsNew.length > 0 &&
      this.productsNew.every((p: any) => p.op_checked);
  }

  // ──────────────────────────────────────────────────────────────────
  // Sub-products expand/collapse (UI only).
  // ──────────────────────────────────────────────────────────────────

  toggleSubProducts(opCode: string): void {
    this.expandedSubProducts[opCode] = !this.expandedSubProducts[opCode];
  }

  isSubProductsExpanded(opCode: string): boolean {
    return !!this.expandedSubProducts[opCode];
  }

  // ──────────────────────────────────────────────────────────────────
  // Product detail sub-modal.
  // ──────────────────────────────────────────────────────────────────

  productDetails(product: Products.IProductToSend): void {
    this.productServ
      .getProduct(product.op_code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((productData: IWsResult) => {
        const id = productData?.data?.[0]?.product_id;
        this.clickedProductId = id ?? null;
        this.closeServ.openProd();
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  get isReady(): boolean {
    return (
      this.assignOrderForm.valid &&
      !this.sending &&
      this.productsNew.length > 0 &&
      this.productsNew.some((p: any) => p.op_checked)
    );
  }

  submit(): void {
    if (!this.isReady) return;
    this.sending = true;

    const f = this.assignOrderForm.value;
    // NB: legacy convention — or_assignment_date is set to today
    // ISO at submit time (different from E.2 which uses
    // orderNew.or_order_date).
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const assignOrder: Orders.IOrderToSend = {
      or_assignment_date: today.toISOString(),
      or_assigned_id: f.or_assigned_id,
      or_order_priority_cod: f.or_order_priority_cod,
      or_carrier_id: f.or_carrier_id,
      or_carrier_desc: this.orderNew.or_carrier_desc,
      or_partial: this.partialOrder ?? 'S',
      // NB: hardcoded by BE convention for manual admin assignments
      or_customer_reference: '123',
      or_agent_id: '1',
      // BE expects op_qta := orQtaNeeded at send time (same legacy
      // mutation logic).
      products: this.productsNew.map((product: any) => {
        product.op_qta = product.orQtaNeeded;
        if (Array.isArray(product?.sub_products)) {
          product.sub_products.forEach((sub: any) => {
            if (sub) {
              sub.osp_qty = (sub.qty_needed ?? 0) * product.orQtaNeeded;
            }
          });
        }
        return product;
      }),
      or_order_number: f.or_order_number,
      or_order_date: this.orderNew.or_order_date,
      or_customer: f.or_customer,
      or_shipping_address: f.or_shipping_address,
      or_shipping_city: f.or_shipping_city,
      or_shipping_prov: f.or_shipping_prov,
      or_shipping_country: f.or_shipping_country,
      or_gamma_id: this.orderNew.or_gamma_id,
      or_customer_code: this.orderNew.or_customer_code,
      or_customer_mail: this.orderNew.or_customer_mail,
      or_customer_phone: this.orderNew.or_customer_phone,
      or_shipping_zip: f.or_shipping_zip,
      or_quote_number: this.orderNew.or_quote_number,
      or_quote_date: this.orderNew.or_quote_date,
      or_subject: this.orderNew.or_subject,
      or_note: f.or_note,
      or_boxes: this.orderNew.or_boxes,
      or_shipping_customer: this.orderNew.or_shipping_customer,
      expected_shipping_date: this.orderNew.expected_shipping_date,
    };

    this.ordersServ
      .addOrder(assignOrder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData) => {
          this.sending = false;
          if (ordersData?.success) {
            this.goBack();
            this.result.emit(true);
            Swal.fire({
              icon: 'success',
              title: 'Assegnato!',
              text: 'Ordine assegnato con successo!',
              timer: 1500,
              timerProgressBar: true,
            });
          } 
          else {
            Swal.fire({
              icon: 'error',
              title: 'Non assegnato',
              text: 'Si è verificato un problema, riprova.',
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.sending = false;
          this.cdr.detectChanges();
          Swal.fire({
            icon: 'error',
            title: 'Non assegnato',
            text: 'Si è verificato un problema, riprova.',
            timer: 1500,
            timerProgressBar: true,
          });
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  goBack(): void {
    this.closeServ.closeAssegnaDet();
  }
  
}
